import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Search, TrendingUp, AlertCircle, CheckCircle2, Package, ChevronRight,
  Upload, ChevronDown, Handshake, UserPlus, Trophy, SkipForward, Globe,
  Building2, Loader2, XCircle, Sparkles, RefreshCw, Clock, Play, Calendar,
  History, Ship, Plane, SlidersHorizontal, ListFilter, Download,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InfoTip from '@/components/InfoTip';
import DateRangeSelect from '@/components/DateRangeSelect';

const STATUS_CONFIG = {
  processing: { label: 'Processing', style: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: Loader2, spin: true },
  ready:      { label: 'Ready to Optimize', style: 'bg-blue-100 text-blue-800 border-blue-300', icon: Play },
  optimized:  { label: 'Optimized', style: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2 },
  failed:     { label: 'Failed', style: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
  unoptimized:{ label: 'Ready to Optimize', style: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertCircle },
  analyzed:   { label: 'Analyzed', style: 'bg-cyan-100 text-cyan-800 border-cyan-300', icon: Package },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unoptimized;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.style} flex items-center w-fit gap-1`} data-testid={`status-badge-${status}`}>
      <Icon className={`h-3 w-3 ${cfg.spin ? 'animate-spin' : ''}`} /> {cfg.label}
    </Badge>
  );
};

const BOMIntelligenceHub = () => {
  const navigate = useNavigate();
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upload');
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  // Optimize dialog
  const [optimizeTarget, setOptimizeTarget] = useState(null);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [originCountries, setOriginCountries] = useState([]);
  const [optParams, setOptParams] = useState({
    exclude_countries: [],
    distributor_type: 'all',
    include_alternates: true,
    alternates_exclude_countries: [],
    imports_as_domestic: true,
    include_spot_prices: true,
    shipment_mode: 'air',
    target_incoterm: 'EXW',
    data_recency: '12months',
    compliances: [],
    temperature_grade: [],
    qualifications: [],
  });

  // Schedule dialog
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleEmail, setScheduleEmail] = useState('');

  // Version history
  const [runsTarget, setRunsTarget] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Inline file upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchBOMs = useCallback(async () => {
    try {
      const response = await api.get('/bom/list');
      setBoms(response.data.boms);
    } catch { toast.error('Failed to load BOMs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBOMs(); }, [fetchBOMs]);

  // Fetch countries for Optimize dialog
  useEffect(() => {
    api.get('/bom/optimization/countries').then(r => {
      setCountries(r.data.countries || []);
      setOriginCountries(r.data.origin_countries || r.data.countries || []);
    }).catch(() => {});
  }, []);

  // Poll processing BOMs
  useEffect(() => {
    const processingBoms = boms.filter(b => b.status === 'processing');
    if (processingBoms.length === 0) return;
    const interval = setInterval(async () => {
      let changed = false;
      for (const b of processingBoms) {
        try {
          const res = await api.get(`/bom/${b.bom_id}/status`);
          if (res.data.status !== 'processing') {
            changed = true;
            if (res.data.status === 'ready') toast.success(`"${b.name}" is ready to optimize`);
            else if (res.data.status === 'failed') toast.error(`"${b.name}" processing failed`);
          }
        } catch { /* ignore */ }
      }
      if (changed) fetchBOMs();
    }, 3000);
    return () => clearInterval(interval);
  }, [boms, fetchBOMs]);

  // Optimize handlers
  const openOptimize = (bom) => {
    setOptimizeTarget(bom);
    setOptParams({
      exclude_countries: [],
      distributor_type: 'all',
      include_alternates: true,
      alternates_exclude_countries: [],
      imports_as_domestic: true,
      include_spot_prices: true,
      shipment_mode: 'air',
      target_incoterm: 'EXW',
      data_recency: '12months',
      compliances: [],
      temperature_grade: [],
      qualifications: [],
    });
  };

  const submitOptimize = async () => {
    if (!optimizeTarget) return;
    setOptimizeLoading(true);
    try {
      const res = await api.post(`/bom/${optimizeTarget.bom_id}/optimize`, optParams);
      toast.success(res.data.message);
      setOptimizeTarget(null);
      // Poll for run completion
      const runId = res.data.run_id;
      const poll = setInterval(async () => {
        try {
          const st = await api.get(`/bom/${optimizeTarget.bom_id}/optimize/runs/${runId}/status`);
          if (st.data.status === 'completed' || st.data.status === 'failed') {
            clearInterval(poll);
            fetchBOMs();
            if (st.data.status === 'completed') toast.success(`Optimization v${res.data.version} completed!`);
            else toast.error('Optimization run failed.');
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Optimization failed'); }
    finally { setOptimizeLoading(false); }
  };

  // Schedule handler
  const openSchedule = (bom) => { setScheduleTarget(bom); setScheduleEmail(''); };
  const submitSchedule = async () => {
    if (!scheduleTarget) return;
    setScheduleLoading(true);
    try {
      const res = await api.post(`/bom/${scheduleTarget.bom_id}/optimize/schedule`, {
        ...optParams, frequency: 'monthly', email: scheduleEmail,
      });
      toast.success(res.data.message);
      setScheduleTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to schedule'); }
    finally { setScheduleLoading(false); }
  };

  // Version history
  const openRuns = async (bom) => {
    setRunsTarget(bom);
    setRunsLoading(true);
    try {
      const res = await api.get(`/bom/${bom.bom_id}/optimize/runs`);
      setRuns(res.data.runs || []);
    } catch { toast.error('Failed to load versions'); }
    finally { setRunsLoading(false); }
  };

  // Inline file upload handler
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-selected
    e.target.value = '';

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, ''));

      const res = await api.post('/bom/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(res.data.message || 'BOM uploaded successfully');
      if (res.data.warnings?.length) {
        res.data.warnings.forEach(w => toast.warning(w));
      }

      // Refresh the BOM list and switch to Processing tab
      await fetchBOMs();
      setFilter('processing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload BOM file');
    } finally {
      setUploading(false);
    }
  };

  const toggleCountry = (c) => {
    setOptParams(prev => ({
      ...prev,
      exclude_countries: prev.exclude_countries.includes(c)
        ? prev.exclude_countries.filter(x => x !== c)
        : [...prev.exclude_countries, c],
    }));
  };

  const toggleAltCountry = (c) => {
    setOptParams(prev => ({
      ...prev,
      alternates_exclude_countries: prev.alternates_exclude_countries.includes(c)
        ? prev.alternates_exclude_countries.filter(x => x !== c)
        : [...prev.alternates_exclude_countries, c],
    }));
  };

  const toggleMultiSelect = (field, val) => {
    setOptParams(prev => ({
      ...prev,
      [field]: prev[field].includes(val)
        ? prev[field].filter(x => x !== val)
        : [...prev[field], val],
    }));
  };

  const shipmentDisabled = optParams.target_incoterm === 'EXW';

  // Filters — always filter from the full boms list
  const filteredBOMs = boms.filter(bom => {
    // Tab filter
    if (filter !== 'all' && filter !== 'upload') {
      if (bom.status !== filter) return false;
    }
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || bom.name?.toLowerCase().includes(q) || (bom.country || '').toLowerCase().includes(q);
    const matchesCountry = countryFilter === 'all' || bom.country === countryFilter;
    const matchesDept = departmentFilter === 'all' || bom.department === departmentFilter;
    let matchesDate = true;
    const bomDate = bom.created_at || bom.uploaded_at || '';
    if (dateRange && dateRange !== 'all') {
      if (dateRange.startsWith('custom:')) {
        const parts = dateRange.split(':');
        matchesDate = bomDate >= parts[1] && bomDate.slice(0, 10) <= parts[2];
      } else {
        const now = new Date();
        const days = { '30d': 30, '90d': 90, '12m': 365, 'ytd': Math.floor((now - new Date(now.getFullYear(), 0, 1)) / 86400000) }[dateRange];
        if (days) matchesDate = bomDate >= new Date(now - days * 86400000).toISOString();
      }
    }
    return matchesSearch && matchesCountry && matchesDept && matchesDate;
  });

  const stats = {
    total: boms.length,
    optimized: boms.filter(b => b.status === 'optimized').length,
    ready: boms.filter(b => b.status === 'ready').length,
    processing: boms.filter(b => b.status === 'processing').length,
    failed: boms.filter(b => b.status === 'failed').length,
    other: boms.filter(b => !['optimized','ready','processing','failed'].includes(b.status)).length,
    totalSavings: boms.filter(b => b.status === 'optimized').reduce((sum, b) => sum + (b.savings_potential || 0), 0),
  };

  const uniqueCountries = [...new Set(boms.map(b => b.country).filter(Boolean))];
  const uniqueDepts = [...new Set(boms.map(b => b.department).filter(Boolean))];

  return (
    <Layout title="BOM Intelligence" subtitle="Upload, process, and optimize your Bills of Materials">
      <div className="space-y-4" data-testid="bom-intelligence-page">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Savings', value: `$${stats.totalSavings.toLocaleString()}`, color: 'from-green-50 to-green-100 border-green-200', text: 'text-green-700', icon: TrendingUp, tid: 'kpi-savings' },
            { label: 'Optimized', value: stats.optimized, color: 'from-emerald-50 to-emerald-100 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2, tid: 'kpi-optimized' },
            { label: 'Ready to Optimize', value: stats.ready, color: 'from-blue-50 to-blue-100 border-blue-200', text: 'text-blue-700', icon: Play, tid: 'kpi-ready' },
            { label: 'Processing', value: stats.processing, color: 'from-indigo-50 to-indigo-100 border-indigo-200', text: 'text-indigo-700', icon: Loader2, tid: 'kpi-processing' },
            { label: 'Failed', value: stats.failed, color: 'from-red-50 to-red-100 border-red-200', text: 'text-red-700', icon: XCircle, tid: 'kpi-failed' },
            { label: 'Total BOMs', value: stats.total, color: 'from-gray-50 to-gray-100 border-gray-200', text: 'text-gray-700', icon: Package, tid: 'kpi-total' },
          ].map(k => (
            <Card key={k.tid} className={`bg-gradient-to-br ${k.color}`} data-testid={k.tid}>
              <CardContent className="pt-4 pb-3">
                <p className="text-[11px] text-gray-600 font-medium flex items-center gap-1"><k.icon className="h-3 w-3" /> {k.label}</p>
                <p className={`text-xl font-bold ${k.text} mt-0.5`}>{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs — fixed position, never shifts */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-gray-100 h-9 w-full justify-start">
            {[
              { v: 'upload', label: 'Upload BOM', icon: Upload },
              { v: 'processing', label: 'Processing', icon: Loader2 },
              { v: 'failed', label: 'Failed', icon: XCircle },
              { v: 'ready', label: 'Ready to Optimize', icon: Play },
              { v: 'optimized', label: 'Optimized', icon: CheckCircle2 },
              { v: 'all', label: 'All', icon: Package },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs h-7 px-3 gap-1.5" data-testid={`tab-${t.v}`}>
                <t.icon className="h-3 w-3" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Search & Filters — only shown when not on Upload tab */}
        {filter !== 'upload' && (
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search BOMs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-9 text-sm bg-white" data-testid="bom-search-input" />
            </div>
            {uniqueCountries.length > 0 && (
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-36 h-9 text-sm bg-white"><Globe className="h-3 w-3 mr-1 text-gray-400" /><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Countries</SelectItem>{uniqueCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {uniqueDepts.length > 0 && (
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-36 h-9 text-sm bg-white"><Building2 className="h-3 w-3 mr-1 text-gray-400" /><SelectValue placeholder="Dept" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Depts</SelectItem>{uniqueDepts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <DateRangeSelect value={dateRange} onChange={setDateRange} testId="bom-date-range" />
          </div>
        )}

        {/* Upload BOM Tab */}
        {filter === 'upload' && (
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="upload-bom-tab">
            <CardContent className="py-12">
              <div className="max-w-lg mx-auto text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-dashed border-green-300 flex items-center justify-center mx-auto mb-5">
                  <Upload className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Your Bill of Materials</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Upload a CSV, XLS, XLSX, or PDF file to start AI-powered analysis, optimization, and risk assessment.
                </p>
                <p className="text-xs text-gray-400 mb-6">
                  MPN, CPN (either one is required), Annual Requirement (Qty), Last Purchase Price, Currency Code, Last Purchase Date, Country of Supply, Incoterms, Make (optional), Supplier (optional)
                </p>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.xls,.xlsx"
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="bom-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-green-600 hover:bg-green-700 text-white h-11 px-8 text-sm font-medium"
                    data-testid="upload-bom-cta-button"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Upload BOM File</>
                    )}
                  </Button>
                </div>
                <a
                  href="/samples/Sample_BOM.csv"
                  download="BOM_Template.csv"
                  className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium hover:underline transition-colors"
                  data-testid="download-bom-template-link"
                >
                  <Download className="h-4 w-4" /> Download BOM Template (.csv)
                </a>
                <div className="mt-8 grid grid-cols-3 gap-4 text-left">
                  {[
                    { icon: Sparkles, title: 'AI Optimization', desc: 'Get cost-saving recommendations powered by market intelligence' },
                    { icon: AlertCircle, title: 'Risk Assessment', desc: 'Identify supply chain risks across lifecycle, sourcing & compliance' },
                    { icon: TrendingUp, title: 'Price Benchmarking', desc: 'Compare your prices against global and domestic benchmarks' },
                  ].map((f, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg border">
                      <f.icon className="h-4 w-4 text-green-600 mb-1.5" />
                      <p className="text-xs font-semibold text-gray-800">{f.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* BOM Table — hidden when Upload tab is active */}
        {filter !== 'upload' && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm flex items-center gap-2">Your BOMs <Badge variant="outline" className="text-[10px] h-4">{filteredBOMs.length}</Badge></CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>
            ) : filteredBOMs.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Package className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No BOMs found matching this filter.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 text-[11px]">
                    <TableHead className="font-semibold">BOM Name</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Components</TableHead>
                    <TableHead className="font-semibold"><span className="flex items-center gap-1">Match Rate <InfoTip text="% of components with pricing + risk data matched." /></span></TableHead>
                    <TableHead className="font-semibold"><span className="flex items-center gap-1">Savings <InfoTip text="Maximum savings potential from optimization." /></span></TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBOMs.map(bom => {
                    const matchedCount = bom.matched_count || 0;
                    const totalCount = bom.total_components || 1;
                    const matchPct = Math.round((matchedCount / totalCount) * 100);
                    const isReady = bom.status === 'ready' || bom.status === 'unoptimized' || bom.status === 'analyzed';
                    const isOptimized = bom.status === 'optimized';
                    const isProcessing = bom.status === 'processing';
                    const isFailed = bom.status === 'failed';
                    const optCount = bom.optimization_count || 0;

                    return (
                      <TableRow key={bom.bom_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/bom/${bom.bom_id}`)} data-testid={`bom-row-${bom.bom_id}`}>
                        <TableCell className="font-medium text-sm">
                          <div className="flex items-center gap-2">
                            {bom.name}
                            {bom.is_live_bom && <Badge className="bg-emerald-100 text-emerald-800 text-[9px] h-4">Live</Badge>}
                          </div>
                        </TableCell>
                        <TableCell><StatusBadge status={bom.status} /></TableCell>
                        <TableCell className="text-xs text-gray-500">{(bom.created_at || bom.uploaded_at) ? new Date(bom.created_at || bom.uploaded_at).toLocaleDateString() : '--'}</TableCell>
                        <TableCell className="text-sm font-medium">{totalCount}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <div className="w-14 bg-gray-200 rounded-full h-1.5"><div className="bg-green-600 h-1.5 rounded-full" style={{ width: `${matchPct}%` }} /></div>
                            <span className="text-xs text-gray-600">{matchedCount}/{totalCount}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-green-700">{bom.status === 'optimized' ? `$${bom.savings_potential?.toLocaleString() || 0}` : <span className="text-gray-300">—</span>}</TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5 justify-end">
                            {/* Optimize button — only enabled when Ready */}
                            {isReady && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 px-2.5 text-xs gap-1" onClick={() => openOptimize(bom)} data-testid={`optimize-btn-${bom.bom_id}`}>
                                <Sparkles className="h-3 w-3" /> Optimize
                              </Button>
                            )}
                            {/* Re-Optimize — after first optimization */}
                            {isOptimized && (
                              <Button size="sm" variant="outline" className="border-green-300 text-green-700 h-7 px-2.5 text-xs gap-1" onClick={() => openOptimize(bom)} data-testid={`reoptimize-btn-${bom.bom_id}`}>
                                <RefreshCw className="h-3 w-3" /> Re-Optimize
                              </Button>
                            )}
                            {isProcessing && (
                              <Button size="sm" disabled className="bg-indigo-50 text-indigo-600 h-7 px-2.5 text-xs gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Processing</Button>
                            )}
                            {isFailed && (
                              <Badge className="bg-red-100 text-red-700 text-xs gap-1 cursor-help" title={bom.failure_reason || 'Processing failed'}><XCircle className="h-3 w-3" /> Failed</Badge>
                            )}
                            {/* Version history — only if optimized */}
                            {optCount > 0 && (
                              <Button size="sm" variant="ghost" className="h-7 px-1.5 text-xs text-gray-500" onClick={() => openRuns(bom)} data-testid={`versions-btn-${bom.bom_id}`}>
                                <History className="h-3 w-3 mr-0.5" /> v{optCount}
                              </Button>
                            )}
                            {/* Schedule */}
                            {(isReady || isOptimized) && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={() => openSchedule(bom)} data-testid={`schedule-btn-${bom.bom_id}`}>
                                <Calendar className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><ChevronDown className="h-3.5 w-3.5 text-gray-400" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onClick={() => navigate(`/bom/${bom.bom_id}`)} className="text-xs"><ChevronRight className="h-3 w-3 mr-2" /> View Details</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')} className="text-xs"><Handshake className="h-3 w-3 mr-2 text-green-600" /> Refer to 1Source</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toast.info('Assign')} className="text-xs"><UserPlus className="h-3 w-3 mr-2 text-blue-600" /> Assign</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* ══ Optimize Dialog ══ */}
      <Dialog open={!!optimizeTarget} onOpenChange={() => setOptimizeTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="optimize-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="h-4 w-4 text-green-600" />
              {optimizeTarget?.optimization_count > 0 ? 'Re-Optimize' : 'Optimize'} BOM
            </DialogTitle>
            <DialogDescription className="text-xs">
              Configure parameters for optimization of <strong>{optimizeTarget?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">

            {/* Row 1: Pricing Data Recency & Distributor Type */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Pricing Data Recency</Label>
                <Select value={optParams.data_recency} onValueChange={v => setOptParams(p => ({...p, data_recency: v}))}>
                  <SelectTrigger className="h-8 mt-1 text-sm" data-testid="data-recency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6months">6 Months</SelectItem>
                    <SelectItem value="12months">12 Months</SelectItem>
                    <SelectItem value="18months">18 Months</SelectItem>
                    <SelectItem value="24months">24 Months</SelectItem>
                    <SelectItem value="30months">30 Months</SelectItem>
                    <SelectItem value="36months">36 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Distributor Type</Label>
                <Select value={optParams.distributor_type} onValueChange={v => setOptParams(p => ({...p, distributor_type: v}))}>
                  <SelectTrigger className="h-8 mt-1 text-sm" data-testid="distributor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="authorized">Authorized Only</SelectItem>
                    <SelectItem value="independent">Independent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Target Incoterm & Shipment Mode */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Target Incoterm</Label>
                <Select value={optParams.target_incoterm} onValueChange={v => setOptParams(p => ({...p, target_incoterm: v, shipment_mode: v === 'EXW' ? 'air' : p.shipment_mode}))}>
                  <SelectTrigger className="h-8 mt-1 text-sm" data-testid="target-incoterm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EXW">EXW</SelectItem>
                    <SelectItem value="CIF">CIF</SelectItem>
                    <SelectItem value="CIF+DUTY">CIF + DUTY</SelectItem>
                    <SelectItem value="DDP">DDP</SelectItem>
                    <SelectItem value="BOM Specific">BOM Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={shipmentDisabled ? 'opacity-50' : ''}>
                <Label className="text-xs font-medium flex items-center gap-1">
                  International Shipment Mode
                  {shipmentDisabled && <span className="text-[10px] text-gray-400 font-normal">(N/A for EXW)</span>}
                </Label>
                <Select value={optParams.shipment_mode} onValueChange={v => setOptParams(p => ({...p, shipment_mode: v}))} disabled={shipmentDisabled}>
                  <SelectTrigger className="h-8 mt-1 text-sm" data-testid="shipment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="air"><span className="flex items-center gap-1"><Plane className="h-3 w-3" /> Air</span></SelectItem>
                    <SelectItem value="sea"><span className="flex items-center gap-1"><Ship className="h-3 w-3" /> Sea</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Exclude Country of Supply */}
            <div>
              <Label className="text-xs font-medium">Exclude Country of Supply</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
                {countries.map(c => (
                  <button
                    key={c}
                    onClick={() => toggleCountry(c)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${optParams.exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                    data-testid={`country-${c}`}
                  >
                    {c} {optParams.exclude_countries.includes(c) ? '\u2715' : ''}
                  </button>
                ))}
              </div>
              {optParams.exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{optParams.exclude_countries.length} excluded</p>}
              <div className="flex gap-1.5 mt-1.5">
                <Input placeholder="Add country manually..." className="h-7 text-xs flex-1" data-testid="manual-country-input-hub"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const v = e.target.value.trim(); if (!countries.includes(v)) setCountries(prev => [...prev, v]); toggleCountry(v); e.target.value = ''; } }} />
              </div>
            </div>

            {/* Include Alternates + Include Spot Prices + Imports as Domestic */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <Switch checked={optParams.include_alternates} onCheckedChange={v => setOptParams(p => ({...p, include_alternates: v, alternates_exclude_countries: v ? p.alternates_exclude_countries : []}))} data-testid="include-alternates" />
                <Label className="text-xs">Include Alternates</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={optParams.include_spot_prices} onCheckedChange={v => setOptParams(p => ({...p, include_spot_prices: v}))} data-testid="include-spot-prices" />
                <Label className="text-xs">Include Spot Prices</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={optParams.imports_as_domestic} onCheckedChange={v => setOptParams(p => ({...p, imports_as_domestic: v}))} data-testid="imports-as-domestic" />
                <Label className="text-xs">Imports as Domestic</Label>
              </div>
            </div>

            {/* Alternates: Exclude Country of Origin (only shown when alternates enabled) */}
            {optParams.include_alternates && (
              <div>
                <Label className="text-xs font-medium">Alternates: Exclude Country of Origin</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
                  {originCountries.map(c => (
                    <button
                      key={`alt-${c}`}
                      onClick={() => toggleAltCountry(c)}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${optParams.alternates_exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                      data-testid={`alt-country-${c}`}
                    >
                      {c} {optParams.alternates_exclude_countries.includes(c) ? '\u2715' : ''}
                    </button>
                  ))}
                </div>
                {optParams.alternates_exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{optParams.alternates_exclude_countries.length} excluded</p>}
                <div className="flex gap-1.5 mt-1.5">
                  <Input placeholder="Add country manually..." className="h-7 text-xs flex-1" data-testid="manual-alt-country-input-hub"
                    onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const v = e.target.value.trim(); if (!originCountries.includes(v)) setOriginCountries(prev => [...prev, v]); toggleAltCountry(v); e.target.value = ''; } }} />
                </div>
              </div>
            )}

            {optParams.include_alternates && (<>
            {/* Compliances */}
            <div>
              <Label className="text-xs font-medium">Compliances</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {['EU-RoHS', 'CN-RoHS', 'REACH', 'PFAS'].map(v => (
                  <button
                    key={v}
                    onClick={() => toggleMultiSelect('compliances', v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.compliances.includes(v) ? 'bg-green-100 text-green-800 border-green-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                    data-testid={`compliance-${v}`}
                  >
                    {v} {optParams.compliances.includes(v) ? '\u2713' : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Temperature Grade */}
            <div>
              <Label className="text-xs font-medium">Temperature Grade</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {['Commercial', 'Commercial Extended', 'Industrial', 'Automotive', 'Military', 'High Temperature', 'Space', 'Medical', 'Telecom', 'Other'].map(v => (
                  <button
                    key={v}
                    onClick={() => toggleMultiSelect('temperature_grade', v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.temperature_grade.includes(v) ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                    data-testid={`temp-grade-${v}`}
                  >
                    {v} {optParams.temperature_grade.includes(v) ? '\u2713' : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Qualifications */}
            <div>
              <Label className="text-xs font-medium">Qualifications</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {['AEC', 'AUTO-QMS', 'AUTO-SAFETY', 'EMC', 'IEC-COMP', 'JEDEC', 'SAFETY-CERT', 'MIL', 'SPEC'].map(v => (
                  <button
                    key={v}
                    onClick={() => toggleMultiSelect('qualifications', v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.qualifications.includes(v) ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                    data-testid={`qualification-${v}`}
                  >
                    {v} {optParams.qualifications.includes(v) ? '\u2713' : ''}
                  </button>
                ))}
              </div>
            </div>
            </>)}

          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOptimizeTarget(null)}>Cancel</Button>
            <Button size="sm" onClick={submitOptimize} disabled={optimizeLoading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-optimize">
              {optimizeLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
              {optimizeTarget?.optimization_count > 0 ? 'Re-Optimize' : 'Optimize'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══ Schedule Dialog ══ */}
      <Dialog open={!!scheduleTarget} onOpenChange={() => setScheduleTarget(null)}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Calendar className="h-4 w-4 text-blue-600" /> Schedule Monthly Optimization</DialogTitle>
            <DialogDescription className="text-xs">Automatically re-optimize <strong>{scheduleTarget?.name}</strong> every month with current parameters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">A summary email will be sent after each scheduled optimization run.</p>
            </div>
            <div>
              <Label className="text-xs">Email for notifications</Label>
              <Input value={scheduleEmail} onChange={e => setScheduleEmail(e.target.value)} placeholder="your@email.com" className="mt-1 h-8" data-testid="schedule-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setScheduleTarget(null)}>Cancel</Button>
            <Button size="sm" onClick={submitSchedule} disabled={scheduleLoading || !scheduleEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-schedule">
              {scheduleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calendar className="h-3 w-3 mr-1" />} Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══ Version History Dialog ══ */}
      <Dialog open={!!runsTarget} onOpenChange={() => setRunsTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-gray-600" /> Optimization History — {runsTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {runsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No optimization runs yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 text-[11px]">
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Incoterm</TableHead>
                    <TableHead>Distributor</TableHead>
                    <TableHead>Alternates</TableHead>
                    <TableHead>Savings</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map(run => (
                    <TableRow key={run.run_id} data-testid={`run-${run.run_id}`}>
                      <TableCell className="font-mono font-semibold text-sm">v{run.version}</TableCell>
                      <TableCell>
                        <Badge className={run.status === 'completed' ? 'bg-green-100 text-green-800' : run.status === 'running' ? 'bg-indigo-100 text-indigo-800' : 'bg-red-100 text-red-800'}>
                          {run.status === 'running' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          {run.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{run.created_at ? new Date(run.created_at).toLocaleString() : '--'}</TableCell>
                      <TableCell className="text-xs">{run.parameters?.target_incoterm}</TableCell>
                      <TableCell className="text-xs">{run.parameters?.distributor_type}</TableCell>
                      <TableCell className="text-xs">{run.parameters?.include_alternates ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-sm font-semibold text-green-700">{run.total_savings != null ? `$${run.total_savings.toLocaleString()}` : '--'}</TableCell>
                      <TableCell className="text-right">
                        {run.status === 'completed' && (
                          <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => navigate(`/bom/${runsTarget?.bom_id}?run=${run.run_id}`)} data-testid={`view-run-${run.run_id}`}>
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BOMIntelligenceHub;
